'use client'

import { ReactNode, useEffect, useRef, useState } from 'react'
import { Button, Carousel, Cell, Divider, Image, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/shared/i18n'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useAsset } from '@marketplace-web/domain/assets'
import FaqEntryUrl from 'components/FaqEntryUrl'
import { FaqEntryType } from 'constants/faq-entry'

import ConversationExample from '../ConversationExample'
import usePhishingFirstTimeListerEducationTracking from '../usePhishingFirstTimeListerEducationTracking'

type Conversation = {
  messages: Array<string>
  avatarImageSrc: string
}

type Slide = {
  title: string
  body: string
  image: string
  conversations?: Array<Conversation>
}

const MANDATORY_INTRO_SLIDE_TIME = 2000

const INTRO_SLIDE: Slide = {
  title: 'intro_slide.title',
  body: 'intro_slide.body',
  image: 'letter-vinted-app.svg',
}

const CAROUSEL_SLIDES: Array<Slide> = [
  {
    title: 'personal_info_slide.title',
    body: 'personal_info_slide.body',
    image: 'phone-lock.svg',
    conversations: [
      {
        messages: ['personal_info_slide.messages.message_1'],
        avatarImageSrc: 'avatar-1.png',
      },
      {
        messages: ['personal_info_slide.messages.message_2'],
        avatarImageSrc: 'avatar-2.png',
      },
      {
        messages: ['personal_info_slide.messages.message_3'],
        avatarImageSrc: 'avatar-3.png',
      },
    ],
  },
  {
    title: 'avoid_links_slide.title',
    body: 'avoid_links_slide.body',
    image: 'phone-hands.svg',
    conversations: [
      {
        messages: ['avoid_links_slide.messages.message_1'],
        avatarImageSrc: 'avatar-4.png',
      },
      {
        messages: ['avoid_links_slide.messages.message_2', 'qr-code.png'],
        avatarImageSrc: 'avatar-5.png',
      },
      {
        messages: ['avoid_links_slide.messages.message_3'],
        avatarImageSrc: 'avatar-6.png',
      },
    ],
  },
  {
    title: 'report_messages_slide.title',
    body: 'report_messages_slide.body',
    image: 'megaphone-messages.svg',
  },
]

type SlidesProps = {
  setIsDialogOpen: (isOpen: boolean) => void
}

const Slides = ({ setIsDialogOpen }: SlidesProps) => {
  const educationContainerRef = useRef<HTMLDivElement>(null)
  const [isButtonEnabled, setIsButtonEnabled] = useState(false)
  const [isEducationStarted, setIsEducationStarted] = useState(false)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)

  const translate = useTranslate('conversation.phishing_first_time_lister_education')

  const asset = useAsset('/assets/phishing-first-time-lister-education')

  const { onSeeTipsClick, onSlideChange, onHelpCenterClick, onDialogClose } =
    usePhishingFirstTimeListerEducationTracking({
      educationContainerRef,
    })

  const lastSlideIndex = CAROUSEL_SLIDES.length - 1
  const isLastSlide = lastSlideIndex === currentSlideIndex

  const helpCentreTranslationValues = {
    'link-help-centre': (chunks: Array<ReactNode>) => (
      <FaqEntryUrl
        key="link-help-centre"
        type={FaqEntryType.SpamPhishing}
        render={faqUrl => (
          <a href={faqUrl} target="_blank" rel="noreferrer" onClick={onHelpCenterClick}>
            {chunks}
          </a>
        )}
      />
    ),
  }

  useEffect(() => {
    const waitingTimer = setTimeout(() => {
      setIsButtonEnabled(true)
    }, MANDATORY_INTRO_SLIDE_TIME)

    return () => clearTimeout(waitingTimer)
  }, [])

  const getButtonTranslationKey = () => {
    if (!isEducationStarted) return 'actions.see_tips'

    if (isLastSlide) return 'actions.close'

    return 'actions.next'
  }

  const handleSlideChange = (index: number) => {
    onSlideChange(currentSlideIndex, index)
    setCurrentSlideIndex(index)
  }

  const handleButtonClick = () => {
    if (!isEducationStarted) {
      setIsEducationStarted(true)
      onSeeTipsClick()

      return
    }

    if (isLastSlide) {
      setIsDialogOpen(false)
      onDialogClose()

      return
    }

    handleSlideChange(currentSlideIndex + 1)
  }

  const renderConversationItem = ({ messages, avatarImageSrc }: Conversation, index: number) => {
    const labelText = translate('scam_message_example', { index: index + 1 })
    const formattedMessages = messages.map(message => {
      if (message.includes('.png')) {
        return <Image src={asset(message)} size={Image.Size.X2Large} />
      }

      return <Text as="span" text={translate(message)} html />
    })

    return (
      <ConversationExample
        key={index}
        label={labelText}
        messages={formattedMessages}
        avatarImageSrc={asset(avatarImageSrc)}
      />
    )
  }

  const renderConversations = (conversations?: Array<Conversation>) => {
    if (!conversations?.length) return null

    return (
      <>
        <Spacer size={Spacer.Size.X3Large} />
        <SeparatedList separator={<Spacer size={Spacer.Size.X2Large} />}>
          {conversations.map(renderConversationItem)}
        </SeparatedList>
      </>
    )
  }

  const renderSlide = ({ title, image, body, conversations }: Slide, index: number) => (
    <Cell key={index}>
      <div className="u-flexbox">
        <Image
          src={asset(image, { theme: { dark: `dark/${image}` } })}
          size={Image.Size.X3Large}
          alt={translate(title)}
          scaling={Image.Scaling.Contain}
        />
      </div>
      <Spacer size={Spacer.Size.Large} />
      <Text
        as="h1"
        text={translate(title)}
        type={Text.Type.Heading}
        alignment={Text.Alignment.Center}
        width={Text.Width.Parent}
      />
      <Spacer size={Spacer.Size.Large} />
      <Text
        as="span"
        text={<div className="u-force-wrap">{translate(body, helpCentreTranslationValues)}</div>}
        type={Text.Type.Body}
        html
      />
      {renderConversations(conversations)}
    </Cell>
  )

  const renderCarousel = () => {
    if (!isEducationStarted) {
      return renderSlide(INTRO_SLIDE, -1)
    }

    const currentSlide = CAROUSEL_SLIDES[currentSlideIndex]

    if (currentSlide) {
      return renderSlide(currentSlide, currentSlideIndex)
    }

    return renderSlide(INTRO_SLIDE, -1)
  }

  const renderCarouselNavigation = () => {
    if (!isEducationStarted) return null

    return (
      <>
        <div className="u-flexbox">
          <Carousel.Navigation
            onBulletSelect={handleSlideChange}
            slidesCount={CAROUSEL_SLIDES.length}
            activeSlide={currentSlideIndex}
            styling={Carousel.Styling.Floating}
          />
        </div>
        <Spacer size={Spacer.Size.Large} />
      </>
    )
  }

  const renderActions = () => (
    <>
      {isEducationStarted ? <Divider /> : null}
      <Cell>
        {renderCarouselNavigation()}
        <Button
          text={translate(getButtonTranslationKey())}
          styling={Button.Styling.Filled}
          onClick={handleButtonClick}
          disabled={!isButtonEnabled}
          testId="phishing-first-time-lister-education-action-button"
        />
      </Cell>
    </>
  )

  return (
    <div className="u-fill-height u-flexbox u-flex-direction-column">
      <div
        key={isEducationStarted ? currentSlideIndex : 'intro'}
        className="phishing-first-time-lister-education__slide"
        ref={educationContainerRef}
      >
        {renderCarousel()}
      </div>
      {renderActions()}
    </div>
  )
}

export default Slides
